import { createSlice, createAsyncThunk, } from '@reduxjs/toolkit';
import { ConfigDomain } from '@pomofocus/core';
import { getApiClient } from '../../utils/apiClient';
import { webPushActions } from './webPushSlice';
// Async thunks
const fetchConfig = createAsyncThunk('config/fetchConfig', async (_, { rejectWithValue }) => {
    try {
        const response = await getApiClient()({
            method: 'get',
            url: '/api/config',
        });
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
const saveConfig = createAsyncThunk('config/saveConfig', async ({ config, onSaved }, { rejectWithValue }) => {
    try {
        await getApiClient()({
            method: 'post',
            url: '/api/config',
            data: { config },
        });
        onSaved();
    }
    catch (error) {
        return rejectWithValue(error);
    }
});
const initialState = {
    configModalVisible: false,
    config: ConfigDomain.DEFAULT_CONFIG,
    configLoaded: false,
    webpushSubs: [],
};
// Config slice
const configSlice = createSlice({
    name: 'config',
    initialState,
    reducers: {
        // Update entire config object
        updateConfigObj: (state, action) => {
            const configObj = action.payload;
            return {
                ...state,
                config: configObj,
            };
        },
        // Toggle config modal visibility
        changeConfigModalVisibility: (state, action) => {
            state.configModalVisible = action.payload.visible;
        },
        // Individual config updates
        updateDarkMode: (state, action) => {
            state.config.darkModeEnabled = action.payload.darkModeEnabled;
        },
        updateAutoStart: (state, action) => {
            state.config.autoStartEnabled = action.payload.autoStartEnabled;
        },
        updateStageSeconds: (state, action) => {
            state.config.stageSeconds = action.payload.stageSeconds;
        },
        updateSoundVolume: (state, action) => {
            state.config.soundVolume = action.payload.soundVolume;
        },
        updateTickingVolume: (state, action) => {
            state.config.tickingVolume = action.payload.tickingVolume;
        },
        updateColorThemes: (state, action) => {
            state.config.colorThemes = action.payload.colorThemes;
        },
        // Reset config to initial state (for error cases)
        resetConfig: () => initialState,
    },
    extraReducers: (builder) => {
        // Fetch config
        builder.addCase(fetchConfig.fulfilled, (state, action) => {
            return {
                ...state, // keep the existing state (e.g. configModalVisible)
                config: action.payload.config,
                webpushSubs: action.payload.webpushSubs,
            };
        });
        // builder.addCase(saveConfig.fulfilled, (state, action) => {
        //   const configObj = action.payload;
        //   const newConfigObj = makeNewConfigObj(configObj);
        //   return {
        //     ...state, // keep the existing state (e.g. configModalVisible)
        //     ...newConfigObj,
        //   };
        // });
        builder.addCase(fetchConfig.rejected, (state) => {
            // Reset to initial state on fetch error
            return initialState;
        });
        builder.addCase(webPushActions.saveWebPushSubscription.fulfilled, (state, action) => {
            state.webpushSubs.push(action.payload.webpushSub);
        });
        builder.addCase(webPushActions.updateWebPushSubscription.fulfilled, (state, action) => {
            state.webpushSubs = state.webpushSubs.map((sub) => sub.auth === action.payload.webpushSub.auth
                ? action.payload.webpushSub
                : sub);
        });
        builder.addCase(webPushActions.removeWebPushSubscription.pending, (state, action) => {
            const { auth } = action.meta.arg;
            state.webpushSubs = state.webpushSubs.filter((sub) => sub.auth !== auth);
        });
    },
});
// Export individual actions
const { updateConfigObj, changeConfigModalVisibility, updateDarkMode, updateAutoStart, updateStageSeconds, updateSoundVolume, updateTickingVolume, updateColorThemes, resetConfig, } = configSlice.actions;
// Grouped exports for better organization
export const configActions = {
    ...configSlice.actions,
    // Sync actions
    updateConfigObj,
    changeConfigModalVisibility,
    updateDarkMode,
    updateAutoStart,
    updateStageSeconds,
    updateSoundVolume,
    updateTickingVolume,
    updateColorThemes,
    resetConfig,
    // Async actions
    fetchConfig,
    saveConfig,
};
export const configSelectors = {
    selectConfigObj: (state) => state.config.config,
    selectWebpushSubs: (state) => state.config.webpushSubs,
};
// Export reducer
export default configSlice.reducer;
